import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, Share2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import { blogPosts } from '../utils/blogData';
import SEO from '../components/SEO';

const ShareButtons = ({ title, slug }) => {
  const pageUrl = encodeURIComponent(`${window.location.origin}/blogs/${slug}`);
  const pageTitle = encodeURIComponent(title);

  const links = [
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`,
      color: '#1877F2',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      ),
    },
    {
      name: 'X (Twitter)',
      href: `https://twitter.com/intent/tweet?url=${pageUrl}&text=${pageTitle}`,
      color: '#000000',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      ),
    },
    {
      name: 'LinkedIn',
      href: `https://www.linkedin.com/shareArticle?mini=true&url=${pageUrl}&title=${pageTitle}`,
      color: '#0A66C2',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
      ),
    },
    {
      name: 'Email',
      href: `mailto:?subject=${pageTitle}&body=Check out this article: ${decodeURIComponent(pageUrl)}`,
      color: '#6c9c48',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
      ),
    },
  ];

  return (
    <div className="flex items-center gap-3" data-testid="blog-share-buttons">
      <Share2 className="w-4 h-4" style={{ color: '#587740' }} />
      <span className="text-sm font-semibold" style={{ color: '#587740' }}>Share:</span>
      {links.map((link) => (
        <a
          key={link.name}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          title={`Share on ${link.name}`}
          className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
          style={{ backgroundColor: link.color }}
          data-testid={`share-${link.name.toLowerCase().replace(/[^a-z]/g, '')}`}
        >
          {link.icon}
        </a>
      ))}
    </div>
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-20 text-center container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#587740' }}>
            Post Not Found
          </h1>
          <p className="text-lg mb-8" style={{ color: '#5e5e5e' }}>
            The blog post you're looking for doesn't exist.
          </p>
          <Link to="/blogs">
            <Button
              className="text-white"
              style={{ backgroundColor: '#81b956' }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <SEO title={post.title} description={post.excerpt} />
      <Header />
      <section className="pt-20">
        <div className="w-full h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
            data-testid="blog-post-hero-image"
          />
        </div>
      </section>

      {/* Article */}
      <article className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{ backgroundColor: '#AABC5A20', color: '#587740' }}
            >
              {post.category}
            </span>
            <span className="flex items-center text-sm" style={{ color: '#999' }}>
              <Calendar className="w-4 h-4 mr-1" />
              {post.date}
            </span>
          </div>

          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8"
            style={{ color: '#587740' }}
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>

          <div
            className="prose prose-lg max-w-none blog-content"
            style={{ color: '#5e5e5e' }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Social Sharing */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <ShareButtons title={post.title} slug={post.slug} />
          </div>

          {/* Navigation */}
          <div className="mt-16 pt-8 border-t border-gray-200 flex justify-between items-center">
            {prevPost ? (
              <Link
                to={`/blogs/${prevPost.slug}`}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                style={{ color: '#587740' }}
                data-testid="blog-prev-link"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Previous Post</span>
              </Link>
            ) : (
              <div />
            )}
            <Link to="/blogs">
              <Button
                variant="outline"
                className="border-2"
                style={{ borderColor: '#81b956', color: '#81b956' }}
                data-testid="blog-back-to-all"
              >
                All Posts
              </Button>
            </Link>
            {nextPost ? (
              <Link
                to={`/blogs/${nextPost.slug}`}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                style={{ color: '#587740' }}
                data-testid="blog-next-link"
              >
                <span className="text-sm font-medium">Next Post</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </article>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default BlogPostPage;
